import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icons from "../icons";
import { formatWhen } from "./ActivityPanel";
import Profile from "./Profile";
import ConfirmModal from "./ConfirmModal";
import { DIALOG_HEIGHT } from "./dialogSize";
import HelpButton from "./HelpDialog";
import * as Loader from "./Loader";
import { useToast } from "./Toast";

/* Settings — the signed-in user's own settings, in two tabs:
     Profile             — user details, password and Jira connectivity (Profile);
     Configuration       — the variables below.
   The screen fits the window: each tab scrolls inside itself.

   A configuration entry ({BASE_URL}, {ADMIN_USER}, …) keeps a value for
   your runs. Values are per user and shown as typed (there are no secrets).
   A Private entry is yours alone; a Public one is seen by everyone, who use
   your value until they set their own.
   Anyone changes a public entry's name, description and value, or deletes
   it (after a confirmation); only its creator makes it private. Create
   Artifact and Execute take values from here (RunVariablesDialog). */

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

export const NAME_RULE = /^[A-Z][A-Z0-9_]{0,99}$/;
export const cleanName = (raw) => String(raw || "").trim().replace(/^\{|\}$/g, "").trim().toUpperCase();

const FILTERS = [
  { id: "all", label: "All" },
  { id: "public", label: "Public" },
  { id: "private", label: "Private" },
];

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25 disabled:bg-slate-50 disabled:text-slate-500";
const labelCls = "mb-1 block text-sm font-medium text-slate-700";

async function call(url, init) {
  const res = await fetch(url, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : `HTTP ${res.status}`);
  return body?.data;
}

const json = (method, data) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(data),
});

/* ------------------------------------------------------------------ */
/*  Small parts                                                        */
/* ------------------------------------------------------------------ */
/* In front of a variable's name: one person for Private, two for Public. */
export function VisibilityIcon({ visibility, className = "h-5 w-5" }) {
  const pub = visibility === "public";
  const Icon = pub ? Icons.Users : Icons.UserCircle;
  const label = pub ? "Public" : "Private";
  return (
    <span
      role="img"
      aria-label={label}
      title={`${label}: ${pub ? "everyone sees it and uses its creator's value until they set their own" : "only its creator sees it"}`}
      className={`inline-flex flex-none ${pub ? "text-brand-600" : "text-slate-500"}`}
    >
      <Icon className={className} aria-hidden="true" />
    </span>
  );
}

export function VisibilityChip({ visibility }) {
  const pub = visibility === "public";
  const Icon = pub ? Icons.Users : Icons.UserCircle;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${
        pub ? "bg-brand-50 text-brand-700 ring-brand-600/20" : "bg-slate-100 text-slate-600 ring-slate-500/20"
      }`}
      title={pub ? "Everyone sees it and uses its creator's value until they set their own" : "Only its creator sees it"}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {pub ? "Public" : "Private"}
    </span>
  );
}

/* What Delete asks: a private entry takes your value with it; a public one
   goes for every user, their private copies stay. */
export function deleteMessage(v) {
  return v.visibility === "public"
    ? `${v.name} is a public configuration. Deleting it removes the configuration for all users but does not affect their ` +
        "private copies. Any runs that depend on it will prompt users to enter the value again."
    : "Deleting this configuration will permanently remove its saved value. You will need to enter the value again " +
        "when executing tests.";
}

/* "name, description and value". */
const listWords = (w) => (w.length > 1 ? `${w.slice(0, -1).join(", ")} and ${w[w.length - 1]}` : w[0]);

/* The value this user gets: their own, else a public variable's shared one. */
export const valueOf = (v) => (v.my_value_set ? v.my_value || "" : v.shared_value ?? "");

function ValueCell({ v }) {
  const value = valueOf(v);
  return value ? <span className="break-all font-mono text-slate-800">{value}</span> : <span className="text-slate-400">Not set</span>;
}

/* Public or private: one switch. */
function PublicSwitch({ checked, onChange, disabled }) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label="Public"
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-60 ${
          checked ? "bg-brand-600" : "bg-slate-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${checked ? "translate-x-5" : "translate-x-0.5"}`}
        />
      </button>
      <span className="text-sm font-medium text-slate-800">{checked ? "Public" : "Private"}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Create / edit a configuration entry                                */
/* ------------------------------------------------------------------ */
/* `initial` is the row being edited (null: create). Anyone changes a public
   entry's name, description and value; only its creator whether it is
   public. `onSave(form)` gets {name, description, visibility, value} and
   resolves false when the user went back to the dialog (a confirmation
   cancelled). */
export function VariableDialog({ initial = null, onClose, onSave }) {
  const editing = Boolean(initial);
  const owner = !editing || initial.is_owner;
  /* Someone else's public variable switched to Private: saving makes the
     user's own private copy (same name and value); the public one stays. */
  const copying = editing && !owner && initial.visibility === "public";
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [isPublic, setIsPublic] = useState(initial?.visibility === "public");
  const [value, setValue] = useState(initial ? valueOf(initial) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const clean = cleanName(name);
  const nameOk = NAME_RULE.test(clean);
  const title = editing ? `Edit Configuration - ${initial.name}` : "Create Configuration";

  const submit = async (e) => {
    e.preventDefault();
    if (!nameOk || busy) return;
    setBusy(true);
    setError("");
    try {
      const saved = await onSave({
        name: clean,
        description: description.trim(),
        visibility: isPublic ? "public" : "private",
        value,
      });
      if (saved === false) setBusy(false); // back to the dialog, edits kept
    } catch (err) {
      setError(err.message || "Could not save the configuration.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" aria-label="Close" tabIndex={-1} onClick={() => !busy && onClose()} className="absolute inset-0 cursor-default bg-overlay/50 backdrop-blur-sm" />
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-label={editing ? "Edit Configuration" : "Create Configuration"}
        className={`relative z-10 flex ${DIALOG_HEIGHT} w-full max-w-lg flex-col overflow-hidden rounded-md bg-card shadow-2xl`}
      >
        {/* Header — the primary theme colour, as on the confirmation dialog;
            no ×: Cancel, Escape or a click outside closes it. */}
        <div className="flex flex-none items-center gap-2 bg-brand-700 px-5 py-3.5">
          <h3 className="flex-1 text-base font-semibold tracking-tight text-white">{title}</h3>
        </div>

        <div className="cq-scroll min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          <div>
            <label htmlFor="var-name" className={labelCls}>
              Name <span className="text-rose-500">*</span>
            </label>
            <input
              id="var-name"
              value={name}
              onChange={(e) => setName(e.target.value.toUpperCase())}
              maxLength={102}
              autoFocus
              className={`${inputCls} font-mono`}
              aria-invalid={name !== "" && !nameOk}
              aria-describedby="var-name-rule"
            />
            {name !== "" && !nameOk && (
              <p id="var-name-rule" className="mt-1 text-[0.6875rem] text-rose-600">
                Use capital letters, digits and _, starting with a letter.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="var-description" className={labelCls}>Description</label>
            <input
              id="var-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="var-value" className={labelCls}>Value</label>
            <input
              id="var-value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              maxLength={2000}
              name="cortex-configuration-value"
              autoComplete="off"
              className={inputCls}
            />
          </div>
          <div>
            <p className={labelCls}>Visibility</p>
            <PublicSwitch checked={isPublic} onChange={setIsPublic} disabled={!owner && !copying} />
            {copying && !isPublic && (
              <p className="mt-1.5 text-[0.75rem] text-slate-600">
                Saving creates your own private {nameOk ? clean : initial.name} with these details. The public {initial.name} stays as it is for everyone else.
              </p>
            )}
          </div>
          {error && (
            <p role="alert" className="flex items-start gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[0.75rem] text-rose-700">
              <Icons.Alert className="mt-0.5 h-3.5 w-3.5 flex-none" /> {error}
            </p>
          )}
        </div>

        <div className="flex flex-none justify-end gap-2.5 border-t border-slate-100 bg-slate-50 px-5 py-3.5">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-300 bg-card px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !nameOk}
            className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy && <Icons.Spinner className="h-4 w-4 animate-spin" />}
            {busy ? "Saving…" : editing ? "Save" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  The Configuration tab: the variables grid                          */
/* ------------------------------------------------------------------ */
export function VariablesSection({ userkey }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null); // {title, message, confirmLabel, busyLabel, tone, icon, run | resolve}
  const [confirmBusy, setConfirmBusy] = useState(false);
  /* While a variable is created or deleted and the grid reloads: the
     loader's {label, sublabel}; then a green toast says it is done. */
  const [working, setWorking] = useState(null);
  const [showToast, toast] = useToast();

  const load = useCallback(async () => {
    if (!userkey) return;
    setError("");
    try {
      setRows((await call(`${API_BASE_URL}variables?userkey=${encodeURIComponent(userkey)}`)) || []);
    } catch (e) {
      setError(e.message || "The configuration could not be loaded.");
      setRows((r) => r || []);
    }
  }, [userkey]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows || []).filter(
      (v) =>
        (filter === "all" || v.visibility === filter) &&
        (!q || v.name.toLowerCase().includes(q) || (v.description || "").toLowerCase().includes(q)),
    );
  }, [rows, query, filter]);

  /* Create: the loader until the grid shows it, then the toast. A refusal
     goes back to the dialog (its error), with what was typed. */
  const create = async (form) => {
    setWorking({ label: `Creating ${form.name}…`, sublabel: "Saving it, then refreshing your configuration" });
    try {
      await call(`${API_BASE_URL}variables`, json("POST", {
        userkey,
        name: form.name,
        description: form.description,
        visibility: form.visibility,
        value: form.value.trim() ? form.value : null,
      }));
    } catch (e) {
      setWorking(null);
      throw e;
    }
    setCreating(false);
    await load();
    setWorking(null);
    showToast(`${form.name} has been created successfully.`);
  };

  /* Ask first, keeping the edit dialog open: true goes on, false goes back to it. */
  const confirmFirst = (opts) => new Promise((resolve) => setConfirm({ ...opts, resolve }));

  /* Edit: the changes to the variable, then its value (set, replace, or
     clear). A public variable's name, description and value are everyone's:
     anyone may change them, once they confirm it. Everything is saved to the
     database; then the dialog closes and, once the grid is read again, a green
     toast says "X has been updated successfully." */
  const saveEdit = (v) => async (form) => {
    /* Someone else's public variable made private: their own private copy,
       with what they typed (the value: else their own, else the creator's). */
    if (!v.is_owner && v.visibility === "public" && form.visibility === "private") {
      await call(`${API_BASE_URL}variables/${v.variablekey}/private-copy`, json("POST", {
        userkey, name: form.name, description: form.description, value: form.value.trim() ? form.value : null,
      }));
      setEditing(null);
      await load();
      showToast(`${form.name} has been updated successfully.`);
      return true;
    }
    const stillPublic = v.visibility === "public" && form.visibility === "public";
    const shared = form.visibility === "public"; // its value is everyone's
    const renamed = form.name !== v.name;
    const described = form.description !== (v.description || "");
    const definitionChanged = renamed || described || (v.is_owner && form.visibility !== v.visibility);
    const before = valueOf(v);
    const typed = form.value.trim() !== "";
    const valueChanged = typed ? form.value !== before : false;
    /* Emptying the value clears it. */
    const clear = !typed && before !== "";

    /* What changes for everyone, and what the change removes, is said first. */
    const everyone = [];
    if (stillPublic && renamed) everyone.push("name");
    if (stillPublic && described) everyone.push("description");
    if (stillPublic && shared && (valueChanged || clear)) everyone.push("value");
    const lost = [];
    if (definitionChanged && v.visibility === "public" && form.visibility === "private" && v.users_with_values > 0) {
      lost.push(`${v.users_with_values} other user${v.users_with_values === 1 ? "'s value is" : "s' values are"} removed: they no longer see it`);
    }
    if (everyone.length || lost.length) {
      const ok = await confirmFirst({
        title: everyone.length ? `Change ${v.name} for all users?` : `Change ${v.name}?`,
        message: [
          everyone.length && `${v.name} is public: saving changes its ${listWords(everyone)} for every user of CortexQA.`,
          lost.length && `If you save this, ${lost.join(", and ")}.`,
        ].filter(Boolean).join(" "),
        confirmLabel: everyone.length ? "Save for all users" : "Save",
        icon: lost.length ? Icons.Alert : null,
      });
      if (!ok) return false;
    }

    if (definitionChanged) {
      await call(`${API_BASE_URL}variables/${v.variablekey}`, json("PUT", v.is_owner
        ? { userkey, name: form.name, description: form.description, visibility: form.visibility }
        : { userkey, name: form.name, description: form.description }));
    }
    if (valueChanged) {
      await call(`${API_BASE_URL}variables/${v.variablekey}/value`, json("PUT", { userkey, value: form.value, shared }));
    } else if (clear) {
      await call(
        `${API_BASE_URL}variables/${v.variablekey}/value?userkey=${encodeURIComponent(userkey)}${shared ? "&shared=true" : ""}`,
        { method: "DELETE" },
      );
    }
    setEditing(null);
    await load();
    // Once the grid shows it; a Save that changed nothing says nothing.
    if (definitionChanged || valueChanged || clear) showToast(`${form.name} has been updated successfully.`);
    return true;
  };

  /* Deleting a public variable affects everyone; a private one only you. */
  const askDelete = (v) => {
    const pub = v.visibility === "public";
    setConfirm({
      title: pub ? `Delete ${v.name} for all users?` : `Delete ${v.name}?`,
      message: deleteMessage(v),
      irreversible: true,
      confirmLabel: "Delete",
      busyLabel: "Deleting…",
      tone: "danger",
      icon: Icons.Trash,
      run: async () => {
        await call(`${API_BASE_URL}variables/${v.variablekey}?userkey=${encodeURIComponent(userkey)}`, { method: "DELETE" });
        await load();
        showToast(`${v.name} has been deleted successfully.`);
      },
      working: { label: `Deleting ${v.name}…`, sublabel: "Deleting it, then refreshing your configuration" },
    });
  };

  /* Confirmed: the dialog closes and the loader stays up until the grid is
     fresh (`working`); a refusal shows in the screen's error line. */
  const runConfirm = async () => {
    const { run, working: label } = confirm;
    setConfirmBusy(true);
    setConfirm(null);
    if (label) setWorking(label);
    try {
      await run();
    } catch (e) {
      setError(e.message || "That did not work.");
    } finally {
      setWorking(null);
      setConfirmBusy(false);
    }
  };

  const th = "sticky top-0 z-10 bg-slate-50 px-3 py-3";
  return (
    <section aria-label="Configuration" className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
      <div className="flex flex-none flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-5 py-2.5">
        <div className="relative min-w-[14rem] flex-1">
          <Icons.Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a name or description"
            aria-label="Search the configuration"
            className="h-9 w-full rounded-lg border border-slate-200 bg-card pl-8 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div role="group" aria-label="Show" className="inline-flex overflow-hidden rounded-lg ring-1 ring-slate-200">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={filter === f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3.5 py-1.5 text-sm font-medium ${filter === f.id ? "bg-brand-600 text-white" : "bg-card text-slate-600 hover:bg-slate-50"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          disabled={!userkey}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-60"
        >
          <Icons.Plus className="h-4 w-4" /> Create
        </button>
      </div>

      {error && (
        <p role="alert" className="mx-5 mt-3 flex flex-none items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <Icons.Alert className="h-3.5 w-3.5 flex-none" /> {error}
        </p>
      )}

      {rows === null ? (
        <Loader.Block label="Loading configuration…" sublabel="Reading your configuration and the public entries" />
      ) : shown.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
          <Icons.Configuration className="h-6 w-6 text-slate-300" />
          <p className="text-sm text-slate-500">
            {rows.length === 0 ? "No configuration yet. Create one to keep a value for your runs." : "No configuration matches."}
          </p>
        </div>
      ) : (
        <div className="cq-scroll min-h-0 flex-1 overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-sm font-bold text-slate-700">
              <tr>
                <th scope="col" className={`${th} pl-5`}>Name</th>
                <th scope="col" className={th}>Description</th>
                <th scope="col" className={th}>Value</th>
                <th scope="col" className={th}>Created by</th>
                <th scope="col" className={th}>Modified by</th>
                <th scope="col" className={th}>Modified date</th>
                <th scope="col" className={`${th} pr-5 text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((v) => (
                <tr key={v.variablekey} aria-label={`${v.name} ${v.visibility}`} className="align-top">
                  <td className="py-3 pl-5 pr-3">
                    <span className="inline-flex items-center gap-2 font-mono text-sm font-semibold text-slate-800">
                      <VisibilityIcon visibility={v.visibility} />
                      {v.name}
                    </span>
                  </td>
                  <td className="max-w-[16rem] px-3 py-3 text-sm text-slate-600">{v.description || <span className="text-slate-300">—</span>}</td>
                  <td className="max-w-[18rem] px-3 py-3 text-sm"><ValueCell v={v} /></td>
                  <td className="px-3 py-3 text-sm text-slate-600">{v.owner?.name || "—"}</td>
                  <td className="px-3 py-3 text-sm text-slate-600">{v.modified_by?.name || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-sm tabular-nums text-slate-600">{formatWhen(v.modifieddate)}</td>
                  <td className="whitespace-nowrap py-3 pl-3 pr-5 text-right">
                    <span className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(v)}
                        aria-label={`Edit ${v.name}`}
                        title="Edit"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-brand-600 hover:bg-brand-50 hover:text-brand-700"
                      >
                        <Icons.Pencil className="h-[1.125rem] w-[1.125rem]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => askDelete(v)}
                        aria-label={`Delete ${v.name}`}
                        title="Delete"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      >
                        <Icons.Trash className="h-[1.125rem] w-[1.125rem]" />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <VariableDialog onClose={() => setCreating(false)} onSave={create} />}
      {working && <Loader.Overlay variant="guide" label={working.label} sublabel={working.sublabel} delay={0} />}
      {toast}
      {/* Keyed: a dialog opened for another variable starts afresh. */}
      {editing && (
        <VariableDialog key={editing.variablekey} initial={editing} onClose={() => setEditing(null)} onSave={saveEdit(editing)} />
      )}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          busyLabel={confirm.busyLabel}
          tone={confirm.tone}
          icon={confirm.icon}
          irreversible={confirm.irreversible}
          busy={confirmBusy}
          onCancel={() => {
            if (confirmBusy) return;
            confirm.resolve?.(false);
            setConfirm(null);
          }}
          onConfirm={confirm.resolve ? () => { confirm.resolve(true); setConfirm(null); } : runConfirm}
        />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  The screen: two tabs                                               */
/* ------------------------------------------------------------------ */
export const TABS = [
  { id: "profile", label: "Profile", Icon: Icons.UserCircle, help: "profile" },
  { id: "config", label: "Configuration", Icon: Icons.Configuration, help: "settings" },
];

export default function SettingsView({ userkey, initialTab = "profile", onBack }) {
  const [tab, setTab] = useState(TABS.some((t) => t.id === initialTab) ? initialTab : "profile");
  const current = TABS.find((t) => t.id === tab);

  /* Arrow keys move between the tabs, as a tab list should. */
  const onKeyDown = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const i = TABS.findIndex((t) => t.id === tab);
    const next = TABS[(i + (e.key === "ArrowRight" ? 1 : TABS.length - 1)) % TABS.length];
    setTab(next.id);
    document.getElementById(`settings-tab-${next.id}`)?.focus();
  };

  /* Fits the window, as the other full screens do: only the tab's own
     sections scroll. */
  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col gap-4">
      <div className="flex flex-none flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-slate-900">Settings</h1>
          <p className="mt-1 text-sm text-slate-500">Your account, and your own configuration for CortexQA.</p>
        </div>
        <HelpButton screen={current.help} />
      </div>

      <div role="tablist" aria-label="Settings" onKeyDown={onKeyDown} className="flex flex-none gap-1 border-b border-slate-200">
        {TABS.map((t) => {
          const selected = t.id === tab;
          return (
            <button
              key={t.id}
              id={`settings-tab-${t.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`settings-panel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setTab(t.id)}
              className={[
                "-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition",
                selected ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-800",
              ].join(" ")}
            >
              <t.Icon className="h-4 w-4" aria-hidden="true" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`settings-panel-${tab}`}
        aria-labelledby={`settings-tab-${tab}`}
        className="flex min-h-0 flex-1 flex-col"
      >
        {tab === "profile" ? <Profile embedded onBack={onBack} /> : <VariablesSection userkey={userkey} />}
      </div>
    </div>
  );
}
